import { z } from 'zod';
import { ArtifactKind, ErrorCode, IsoDateTime, JobStatus, JobType, PackageId, Uuid, } from './primitives.js';
import { JobOptions, JobStep } from './jobs.js';
/**
 * Dispatch is pull: the runner claims work, the control plane never calls the runner.
 * See Detachly-doc/02-architecture/contracts.md.
 */
export const ClaimRequest = z.object({
    runnerId: z.string().min(1),
    types: z.array(JobType).min(1),
    max: z.number().int().min(1).max(5).default(1),
    capabilities: z
        .object({
        playwright: z.boolean().default(false),
        nodeMajor: z.number().int(),
    })
        .optional(),
});
/** Plaintext key, valid only for this lease. Memory-only on the runner — never persisted. */
export const FramerKeyEnvelope = z.object({
    apiKey: z.string().min(1),
    projectUrlOrId: z.string().min(1),
    expiresAt: IsoDateTime,
});
export const JobBudget = z.object({
    maxUsd: z.number().positive(),
    maxRepairAttempts: z.number().int().nonnegative(),
});
export const ClaimedJob = z.object({
    jobId: Uuid,
    type: JobType,
    projectId: Uuid,
    userId: z.string().min(1),
    options: JobOptions,
    leaseToken: z.string().min(1),
    leaseExpiresAt: IsoDateTime,
    attempt: z.number().int().min(1),
    /** e.g. "tenants/<userId>/jobs/<jobId>/" */
    storagePrefix: z.string().min(1),
    scan: z
        .object({
        scanSnapshotId: Uuid,
        storagePrefix: z.string().min(1),
        /**
         * When the scan was taken. Codegen stamps this instead of the wall clock, so rebuilding the
         * same scan produces the same bytes (AC-A4).
         */
        scannedAt: IsoDateTime,
    })
        .nullable(),
    /** null when the job needs no live extract */
    framer: FramerKeyEnvelope.nullable(),
    entitlement: z
        .object({
        packageId: PackageId,
        aiPagesRemaining: z.number().int().min(0),
        retriesRemaining: z.number().int().min(0),
    })
        .nullable(),
    budget: JobBudget,
});
export const ClaimResponse = z.object({
    jobs: z.array(ClaimedJob),
});
export const ProgressRequest = z.object({
    leaseToken: z.string().min(1),
    status: z.literal('running'),
    steps: z.array(JobStep),
    message: z.string().max(500).optional(),
    /** extractMs, tokensIn, usdSpent… */
    metrics: z.record(z.string(), z.number()).optional(),
});
export const ArtifactUpload = z.object({
    kind: ArtifactKind,
    storageKey: z.string().min(1),
    bytes: z.number().int().nonnegative(),
    sha256: z.string().length(64),
});
export const CompleteRequest = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('succeeded'),
        leaseToken: z.string().min(1),
        /**
         * Usually at least one downloadable file, but a `scan` job produces none: its result is the
         * ScanSnapshot row plus raw dumps under the scan prefix, which are not customer artifacts.
         */
        artifacts: z.array(ArtifactUpload).default([]),
        meta: z.object({
            irVersion: z.string().optional(),
            models: z.record(z.string(), z.string()).optional(),
            timingsMs: z.record(z.string(), z.number()).optional(),
            usdSpent: z.number().nonnegative().optional(),
            playbookIds: z.array(z.string()).optional(),
        }),
    }),
    z.object({
        status: z.literal('failed'),
        leaseToken: z.string().min(1),
        errorCode: ErrorCode,
        errorMessage: z.string().max(2000),
        /** partial evidence, e.g. a redacted build log */
        artifacts: z.array(ArtifactUpload).default([]),
        meta: z.object({ usdSpent: z.number().nonnegative().optional() }).default({}),
    }),
]);
/**
 * Sent when a runner gives a job back without finishing it — a rolling deploy, for example.
 * The job returns to the queue immediately instead of waiting for the lease to expire,
 * and the attempt counter is untouched because this is not a job failure.
 */
export const ReleaseRequest = z.object({
    leaseToken: z.string().min(1),
    reason: z.enum(['shutdown', 'capacity']),
});
/** Answer to a heartbeat: `leaseExpiresAt` is how long the runner may keep working. */
export const ProgressResponse = z.object({
    ok: z.literal(true),
    leaseExpiresAt: IsoDateTime.nullable(),
});
export const ReleaseResponse = z.object({
    jobId: Uuid,
    status: JobStatus,
    attempt: z.number().int().min(1),
});
export const CompleteResponse = z.object({
    jobId: Uuid,
    status: z.enum(['succeeded', 'failed']),
    /** true when this call was a no-op replay of an already terminal job */
    idempotentReplay: z.boolean(),
});
//# sourceMappingURL=internal.js.map